import { TokenStorage } from './TokenStorage';
import type { Model } from '@/shared/types/api.types';
import type { Assistant } from '@/shared/services/AssistantService';
import type { GTCTenantAssignment } from '../types/auth.types';
import { authConfig } from '@/shared/config/auth.config';
import { currentDate } from '@/shared/utils/dateUtils';
import {
  getCsrfToken,
  fetchCsrfToken,
  clearCsrfToken,
} from '@/shared/services/HttpClient';

type GTCHttpClientInstance =
  (typeof import('@/shared/services/GTCHttpClient'))['gtcHttpClient'];

async function getGTCHttpClient(): Promise<GTCHttpClientInstance> {
  const module = await import('@/shared/services/GTCHttpClient');
  return module.gtcHttpClient;
}

let gtcHttpClientSingleton: GTCHttpClientInstance | null = null;

async function getGTCHttpClientSingleton(): Promise<GTCHttpClientInstance> {
  if (gtcHttpClientSingleton) {
    return gtcHttpClientSingleton;
  }
  gtcHttpClientSingleton = await getGTCHttpClient();
  return gtcHttpClientSingleton;
}

function mapTenantAssignment(
  assignment:
    | {
        tenant?: string | null;
        tenant_slug?: string | null;
        tenant_name?: string | null;
        tenant_uuid?: string | null;
        is_active?: boolean | null;
        created_at?: string | null;
        role?: string | null;
        roles?: string[] | null;
        role_assignment_created?: boolean;
      }
    | null
    | undefined
): GTCTenantAssignment | null {
  if (!assignment) {
    return null;
  }

  return {
    tenant: assignment.tenant ?? null,
    tenantSlug: assignment.tenant_slug ?? assignment.tenant ?? null,
    tenantName: assignment.tenant_name ?? null,
    tenantUuid: assignment.tenant_uuid ?? null,
    isActive: assignment.is_active ?? null,
    createdAt: assignment.created_at ?? null,
    role: assignment.role ?? null,
    roles: assignment.roles ?? (assignment.role ? [assignment.role] : null),
    roleAssignmentCreated: assignment.role_assignment_created ?? false,
  };
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  defaultModel?: Model;
  defaultAssistant?: Assistant | null;
  profilePhotoUrl?: string;
  customInstructions?: string;
  creativityLevel?: string;
  // GTC specific fields
  gtcRoles?: string[];
  subscriptionTier?: string;
  tenantSlug?: string;
  gtcTenantAssignment?: GTCTenantAssignment | null;
}

export interface AuthToken {
  token: string;
  user: User;
  createdAt: string;
  expiresAt?: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: number;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
    default_model?: Model;
    default_assistant?: Assistant | null;
    profile_photo_url?: string;
    custom_instructions?: string;
    creativity_level?: string;
    gtc_roles?: string[];
    subscription_tier?: string | null;
    tenant_slug?: string | null;
    gtc_tenant_assignment?: {
      tenant?: string | null;
      tenant_slug?: string | null;
      tenant_name?: string | null;
      tenant_uuid?: string | null;
      is_active?: boolean | null;
      created_at?: string | null;
      role?: string | null;
      roles?: string[] | null;
      role_assignment_created?: boolean;
    } | null;
  };
  expires_at?: string; // Token expiry timestamp from backend
  created_at?: string;
}

export interface SessionInfo {
  authenticated: boolean;
  user?: User;
  sessionExpiry?: string;
  oidcAuthenticated?: boolean;
}

class GCPAuthService {
  private baseUrl: string;
  private tokenStorage: TokenStorage;

  constructor() {
    this.baseUrl = authConfig.apiBaseUrl;
    this.tokenStorage = new TokenStorage();
  }

  private resolveAuthCallbackUrl(): string {
    const callbackPath =
      authConfig.redirectPaths.authCallback || '/auth/callback';

    // Allow absolute callback URLs from config/env
    if (
      callbackPath.startsWith('http://') ||
      callbackPath.startsWith('https://')
    ) {
      return callbackPath;
    }

    if (typeof window === 'undefined') {
      // Fallback during SSR/tests – return relative path
      return callbackPath.startsWith('/') ? callbackPath : `/${callbackPath}`;
    }

    try {
      return new URL(callbackPath, window.location.origin).toString();
    } catch {
      const normalizedPath = callbackPath.startsWith('/')
        ? callbackPath
        : `/${callbackPath}`;
      return `${window.location.origin}${normalizedPath}`;
    }
  }

  private buildLoginUrl(provider: 'google' | 'microsoft'): string {
    const callbackUrl = this.resolveAuthCallbackUrl();
    const params = new URLSearchParams({
      next: callbackUrl,
    });

    const endpoint =
      provider === 'microsoft'
        ? authConfig.endpoints.loginMicrosoft ||
          authConfig.endpoints.multiProviderLogin
        : authConfig.endpoints.loginGoogle ||
          authConfig.endpoints.multiProviderLogin ||
          authConfig.endpoints.oidcAuthenticate;

    const loginUrl = `${this.baseUrl}${endpoint ?? '/auth/login/'}?${params.toString()}`;
    return loginUrl;
  }

  initiateOIDCLogin(provider: 'google' | 'microsoft' = 'google'): void {
    // The backend will handle the OIDC callback and redirect back to frontend with token
    // The backend's custom_oidc_callback will add the token to the URL params

    const loginUrl = this.buildLoginUrl(provider);
    window.location.href = loginUrl;
  }

  async handleCallback(): Promise<AuthToken | null> {
    try {
      const token = await this.requestToken();
      if (token) {
        this.tokenStorage.setToken(token);

        // Fetch CSRF token from the backend for cross-origin requests
        await fetchCsrfToken();

        // Set GTC context if available
        if (token.user) {
          const gtcHttpClient = await getGTCHttpClientSingleton();
          // Set user ID for GTC RBAC checks
          gtcHttpClient.setGTCUserId(
            token.user.email || token.user.username || token.user.id
          );

          // Set tenant if available
          if (token.user.tenantSlug) {
            gtcHttpClient.setTenantSlug(token.user.tenantSlug);
          }

          // Always use Bearer format for GTC integration
          gtcHttpClient.setUseBearerFormat(true);
        }

        return token;
      }
      return null;
    } catch (error) {
      console.error('Error handling auth callback:', error);
      return null;
    }
  }

  async requestToken(): Promise<AuthToken | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      // Ensure CSRF token is available (fetches from backend in cross-origin setups)
      await fetchCsrfToken();
      const csrfToken = getCsrfToken();
      const response = await fetch(`${this.baseUrl}/auth/token/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error(
            'Access denied. You may not have permission to access this resource.'
          );
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        throw new Error(`Failed to get token: ${response.statusText}`);
      }

      const data = await response.json();

      return {
        token: '', // Token is in HttpOnly cookie, not accessible via JS
        user: {
          id: String(data.user.id),
          username: data.user.username,
          email: data.user.email,
          firstName: data.user.first_name,
          lastName: data.user.last_name,
          defaultModel: data.user.default_model,
          defaultAssistant: data.user.default_assistant ?? null,
          profilePhotoUrl: data.user.profile_photo_url,
          customInstructions: data.user.custom_instructions,
          creativityLevel: data.user.creativity_level,
          gtcRoles: data.user.gtc_roles ?? undefined,
          subscriptionTier: data.user.subscription_tier ?? undefined,
          tenantSlug: data.user.tenant_slug ?? undefined,
          gtcTenantAssignment: mapTenantAssignment(
            data.user.gtc_tenant_assignment
          ),
        },
        createdAt: currentDate().toISOString(),
        expiresAt: data.expires_at,
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error(
            '[GCPAuthService] Token request timeout - backend may be slow'
          );
          throw new Error(
            'Authentication request timed out. Please try again.'
          );
        } else if (
          error instanceof TypeError &&
          error.message.includes('fetch')
        ) {
          console.error(
            '[GCPAuthService] Network error - backend may be offline'
          );
          throw new Error(
            'Cannot connect to authentication service. Please check your connection.'
          );
        }
      }

      console.error('[GCPAuthService] Error requesting token:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to obtain authentication token');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Auth token is sent via HttpOnly cookie automatically
      const response = await fetch(`${this.baseUrl}/auth/me/`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          this.tokenStorage.clearToken();
        }
        return null;
      }

      const data = await response.json();

      const currentUser = {
        id: String(data.id),
        username: data.username,
        email: data.email,
        firstName: data.first_name,
        lastName: data.last_name,
        defaultModel: data.default_model,
        defaultAssistant: data.default_assistant ?? null,
        profilePhotoUrl: data.profile_photo_url,
        customInstructions: data.custom_instructions,
        creativityLevel: data.creativity_level,
        // GTC specific fields
        gtcRoles: data.gtc_roles ?? undefined,
        subscriptionTier: data.subscription_tier ?? null,
        tenantSlug: data.tenant_slug ?? null,
        gtcTenantAssignment: mapTenantAssignment(data.gtc_tenant_assignment),
      };
      return currentUser;
    } catch {
      // Don't clear token on network errors - it might still be valid
      return null;
    }
  }

  async refreshToken(): Promise<AuthToken | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Ensure CSRF token is available (fetches from backend in cross-origin setups)
      await fetchCsrfToken();
      const csrfToken = getCsrfToken();
      const response = await fetch(`${this.baseUrl}/auth/token/rotate/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          this.tokenStorage.clearToken();
        }
        return null;
      }

      const data = await response.json();

      const newToken: AuthToken = {
        token: '', // Token is in HttpOnly cookie
        user: data.user
          ? {
              id: String(data.user.id),
              username: data.user.username,
              email: data.user.email,
              firstName: data.user.first_name,
              lastName: data.user.last_name,
              defaultModel: data.user.default_model,
              defaultAssistant: data.user.default_assistant ?? null,
              customInstructions: data.user.custom_instructions,
              creativityLevel: data.user.creativity_level,
              profilePhotoUrl: data.user.profile_photo_url,
              gtcRoles: data.user.gtc_roles ?? undefined,
              subscriptionTier: data.user.subscription_tier ?? undefined,
              tenantSlug: data.user.tenant_slug ?? undefined,
              gtcTenantAssignment: mapTenantAssignment(
                data.user.gtc_tenant_assignment
              ),
            }
          : (this.tokenStorage.getToken()?.user ?? {
              id: '',
              username: '',
              email: '',
            }),
        createdAt: currentDate().toISOString(),
        expiresAt: data.expires_at,
      };

      this.tokenStorage.setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('[GCPAuthService] Error refreshing token:', error);
      return null;
    }
  }

  async logout(): Promise<{ success: boolean; errors: string[] }> {
    const errors: string[] = [];

    // Helper function to add timeout to fetch requests
    const fetchWithTimeout = async (
      url: string,
      options: RequestInit,
      timeout = 30000
    ): Promise<Response> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if ((error as Error).name === 'AbortError') {
          throw new Error('Request timeout - backend may be unavailable');
        }
        if (error instanceof TypeError && error.message.includes('fetch')) {
          throw new Error('Network error - backend may be offline');
        }
        throw error;
      }
    };

    // Helper function to retry with exponential backoff
    const retryWithBackoff = async (
      fn: () => Promise<void>,
      maxRetries = 3,
      initialDelay = 1000
    ): Promise<void> => {
      let lastError: Error | undefined;

      for (let i = 0; i < maxRetries; i++) {
        try {
          await fn();
          return;
        } catch (error) {
          lastError = error as Error;

          if (
            lastError.message &&
            (lastError.message.includes('No token found') ||
              lastError.message.includes('already revoked'))
          ) {
            return;
          }

          if (i < maxRetries - 1) {
            await new Promise(resolve =>
              setTimeout(resolve, initialDelay * Math.pow(2, i))
            );
          }
        }
      }

      if (lastError) {
        throw lastError;
      }
    };

    // Parallel logout operations (all use credentials: 'include' for cookie-based auth)
    const logoutOperations: Promise<void>[] = [];

    // 1. Token revocation with retry
    logoutOperations.push(
      retryWithBackoff(async () => {
        const csrfToken = getCsrfToken();
        const response = await fetchWithTimeout(
          `${this.baseUrl}/auth/token/revoke/`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
            },
          },
          5000
        );

        if (response.ok || response.status === 404 || response.status === 401) {
          return;
        }

        throw new Error(
          `Token revocation failed: ${response.status} ${response.statusText}`
        );
      }).catch(() => {
        // Network issues are non-critical — local state clearing is what matters
      })
    );

    // 2. Session logout with retry
    logoutOperations.push(
      retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${this.baseUrl}/auth/logout/`,
          {
            method: 'GET',
            credentials: 'include',
          },
          5000
        );

        if (
          !response.ok &&
          response.status !== 404 &&
          response.status !== 401
        ) {
          throw new Error(
            `Session logout failed: ${response.status} ${response.statusText}`
          );
        }
      }).catch(() => {
        // Network issues are non-critical
      })
    );

    // Execute all logout operations in parallel
    await Promise.allSettled(logoutOperations);

    // Always clear local token storage, regardless of API call results
    this.tokenStorage.clearToken();
    clearCsrfToken();

    // Clear GTC context
    try {
      const gtcHttpClient = await getGTCHttpClientSingleton();
      gtcHttpClient.clearGTCData();
    } catch {
      // Ignore GTC clearing errors
    }

    return {
      success: errors.length === 0,
      errors,
    };
  }

  async checkAuthStatus(): Promise<SessionInfo> {
    // Auth token is sent via HttpOnly cookie automatically
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${this.baseUrl}/api/auth/status/`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        // Fetch CSRF token for cross-origin requests if authenticated
        if (data.authenticated) {
          fetchCsrfToken();
        }

        return {
          authenticated: data.authenticated,
          user: data.user
            ? {
                id: String(data.user.id),
                username: data.user.username,
                email: data.user.email,
                firstName: data.user.first_name,
                lastName: data.user.last_name,
                defaultAssistant: data.user.default_assistant ?? null,
              }
            : undefined,
          sessionExpiry: data.session_expiry,
          oidcAuthenticated: data.oidc_authenticated,
        };
      }

      if (response.status === 401) {
        this.tokenStorage.clearToken();
      }
    } catch (error) {
      console.error('[GCPAuthService] Error checking auth status:', error);
    }

    return { authenticated: false };
  }

  getStoredToken(): AuthToken | null {
    return this.tokenStorage.getToken();
  }

  setStoredToken(token: AuthToken): void {
    this.tokenStorage.setToken(token);
  }

  getAuthHeader(): Record<string, never> {
    // Auth token is now in HttpOnly cookie — no Authorization header needed
    return {};
  }

  isTokenExpired(): boolean {
    const token = this.tokenStorage.getToken();
    if (!token || !token.expiresAt) return false;

    return new Date(token.expiresAt) <= currentDate();
  }

  async loginWithCredentials(
    username: string,
    password: string
  ): Promise<AuthToken | null> {
    try {
      // Ensure CSRF token is available (fetches from backend in cross-origin setups)
      await fetchCsrfToken();
      const csrfToken = getCsrfToken();

      // Backend sets HttpOnly cookie on successful login
      const response = await fetch(`${this.baseUrl}/auth/token/login/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error(`Login failed: ${response.statusText}`);
      }

      const data: AuthResponse = await response.json();

      const authToken: AuthToken = {
        token: '', // Token is in HttpOnly cookie
        user: {
          id: String(data.user.id),
          username: data.user.username,
          email: data.user.email,
          firstName: data.user.first_name,
          lastName: data.user.last_name,
          defaultModel: data.user.default_model,
          defaultAssistant: data.user.default_assistant ?? null,
          profilePhotoUrl: data.user.profile_photo_url,
          customInstructions: data.user.custom_instructions,
          creativityLevel: data.user.creativity_level,
          gtcRoles: data.user.gtc_roles ?? undefined,
          subscriptionTier: data.user.subscription_tier ?? undefined,
          tenantSlug: data.user.tenant_slug ?? undefined,
          gtcTenantAssignment: mapTenantAssignment(
            data.user.gtc_tenant_assignment
          ),
        },
        createdAt: data.created_at || currentDate().toISOString(),
        expiresAt: data.expires_at,
      };

      this.tokenStorage.setToken(authToken);
      return authToken;
    } catch (error) {
      console.error('Error logging in with credentials:', error);
      return null;
    }
  }
}

export const authService = new GCPAuthService();
